import { ReactNode } from "react";
import { Config, Data } from "@measured/puck";

import Image from "./blocks/Image";
import { VideoPlayer } from "./blocks/VideoPlayer";
import { HeroSection, HeroSectionProps } from "./blocks/HeroSection";
import { ArticlesSection, ArticlesSectionProps } from "./blocks/ArticlesSection";
import { IndicatorsSection, IndicatorsSectionProps } from "./blocks/IndicatorsSection";
import { Typography } from "./blocks/Typography";
import { Separator, SeparatorProps } from "./blocks/Separator";
import { Space, SpaceProps } from "./blocks/Space";
import { PageBreak, PageBreakProps } from "./blocks/PageBreak";
import { Grid, GridBlockProps } from "./blocks/Grid";
import ActionPlan, { ActionPlanConfigProps } from "./blocks/ActionPlan";
import ActionPlanActionsList, { ActionPlanActionsListConfigProps } from "./blocks/ActionPlanActionsList";
import TokenizedText, { TokenizedTextProps } from "./blocks/TokenizedText";
import AiComment, { AiCommentBlockProps } from "./blocks/AiComment";
import { ImageProps } from "./blocks/Image/types";
import { VideoPlayerProps } from "./blocks/VideoPlayer/types";
import { TypographyProps } from "./blocks/Typography/Typography";
import SupersetChart, { SupersetChartBlockProps } from "./blocks/SupersetChartBlock";
import SupersetDashboard, { SupersetDashboardBlockProps } from "./blocks/SupersetDashboardBlock";
import CallToAction, { CallToActionProps } from "./blocks/CallToAction";
import Icon, { IconProps } from "./blocks/Icon";
import PdfExport, { PdfExportProps } from "./blocks/PdfExport";
import TrajectoryChartblock, { TrajectoryChartblockConfigProps } from "./blocks/TrajectoryChartBlock";
import ScoreChartblock, { ScoreChartblockConfigProps } from "./blocks/ScoreChartBlock";
import FoodsForm, { SsqAnswersConfigProps as FoodsFormConfigProps } from "./blocks/SsqAnswers";
import Root, { RootProps } from "./root";
import PdfSettingsField from "./fields/PdfSettingsField";
import PdfHeaderField from "./fields/PdfHeaderField";
import PdfFooterField from "./fields/PdfFooterField";
import PdfDownloadAvailabilityField from "./fields/PdfDownloadAvailabilityField";

export type Props = {
    Image: ImageProps;
    VideoPlayer: VideoPlayerProps;
    Typography: TypographyProps;
    HeroSection: HeroSectionProps;
    ArticlesSection: ArticlesSectionProps;
    IndicatorsSection: IndicatorsSectionProps;
    Separator: SeparatorProps;
    Space: SpaceProps;
    PageBreak: PageBreakProps;
    Grid: GridBlockProps;
    ActionPlan: ActionPlanConfigProps;
    ActionPlanActionsList: ActionPlanActionsListConfigProps;
    TokenizedText: TokenizedTextProps;
    AiComment: AiCommentBlockProps;
    SupersetChart: SupersetChartBlockProps;
    SupersetDashboard: SupersetDashboardBlockProps;
    CallToAction: CallToActionProps;
    Icon: IconProps;
    PdfExport: PdfExportProps;
    TrajectoryChartblock: TrajectoryChartblockConfigProps;
    ScoreChartblock: ScoreChartblockConfigProps;
    FoodsForm: FoodsFormConfigProps;
};

export type PageConfig = Config<Props, RootProps, "layout" | "media" | "content" | "interactive" | "data">;

export type PageData = Data<Props, RootProps>;

export const rootConfig: PageConfig = {
    root: {
        render: function RootWrapper({ children, ...props }: RootProps & { children: ReactNode }) {
            return <Root {...props}>{children}</Root>;
        },
        fields: {
            downloadOnlyOnSsqValidated: {
                type: "custom",
                label: "Availability",
                render: ({ value, onChange, readOnly }) => (
                    <PdfDownloadAvailabilityField
                        value={value as boolean | undefined}
                        onChange={onChange as (value: boolean) => void}
                        readOnly={readOnly}
                    />
                ),
            },
            pdfSettings: {
                type: "custom",
                label: "Page Settings",
                render: ({ value, onChange, readOnly }) => (
                    <PdfSettingsField value={value} onChange={onChange} readOnly={readOnly} />
                ),
            },
            pdfHeader: {
                type: "custom",
                label: "Header",
                render: ({ value, onChange, readOnly }) => (
                    <PdfHeaderField value={value} onChange={onChange} readOnly={readOnly} />
                ),
            },
            pdfFooter: {
                type: "custom",
                label: "Footer",
                render: ({ value, onChange, readOnly }) => (
                    <PdfFooterField value={value} onChange={onChange} readOnly={readOnly} />
                ),
            },
        },
        resolveFields: (data, { fields }) => {
            const isPdfMode = data.props?.pageType === "PDF";

            if (!isPdfMode) {
                return {
                    ...fields,
                    downloadOnlyOnSsqValidated: undefined,
                    pdfSettings: undefined,
                    pdfHeader: undefined,
                    pdfFooter: undefined,
                };
            }

            return fields;
        },
        defaultProps: {
            downloadOnlyOnSsqValidated: true,
            pdfSettings: {
                format: "A3",
                landscape: "portrait",
                marginsMm: {
                    top: 12,
                    right: 12,
                    bottom: 12,
                    left: 12,
                },
            },
            pdfHeader: {
                enabled: false,
                logoHeight: 30,
                alignment: "left",
            },
            pdfFooter: {
                enabled: false,
                showPageNumbers: true,
                pageNumberFormat: "pageOfTotal",
                alignment: "spread",
            },
        },
    },
    categories: {
        data: {
            title: "Data",
            components: [
                "ActionPlan",
                "ActionPlanActionsList",
                "SupersetChart",
                "SupersetDashboard",
                "TrajectoryChartblock",
                "ScoreChartblock",
                "FoodsForm",
            ],
        },
        layout: {
            title: "Layout",
            components: ["Separator", "Space", "PageBreak", "Grid"],
        },
        interactive: {
            title: "Atoms",
            components: ["Typography", "TokenizedText", "AiComment", "CallToAction", "PdfExport", "Icon"],
        },
        media: {
            title: "Media",
            components: ["Image", "VideoPlayer"],
        },
        content: {
            title: "Content",
            components: ["HeroSection", "ArticlesSection", "IndicatorsSection"],
        },
    },
    components: {
        VideoPlayer: VideoPlayer,
        Typography: Typography,
        HeroSection: HeroSection,
        ArticlesSection: ArticlesSection,
        IndicatorsSection: IndicatorsSection,
        Separator: Separator,
        Space: Space,
        PageBreak: PageBreak,
        Grid: Grid,
        ActionPlan: ActionPlan,
        ActionPlanActionsList: ActionPlanActionsList,
        TokenizedText: TokenizedText,
        AiComment: AiComment,
        SupersetChart: SupersetChart,
        SupersetDashboard: SupersetDashboard,
        CallToAction: CallToAction,
        PdfExport: PdfExport,
        TrajectoryChartblock: TrajectoryChartblock,
        ScoreChartblock: ScoreChartblock,
        FoodsForm: FoodsForm,
        Icon: Icon,
        Image: Image,
    },
};

export default rootConfig;
